import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { api, isSuperAdmin } from "../utils/helpers";

// An explicit empty canonical array takes precedence over a legacy assignment.
export const userCustomerIds = account => Array.isArray(account?.customerIds)
  ? account.customerIds : account?.customerId ? [account.customerId] : [];

// Shared by the two user forms; customer membership never changes access grants.
export function useUserCustomers(user) {
  const enabled = isSuperAdmin(user);
  const owner = enabled ? user?.id : null;
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({ owner: null, attempt: -1, status: "loading", list: [] });
  useEffect(() => {
    if (!enabled) return;
    let active = true;
    setState({ owner, attempt, status: "loading", list: [] });
    api("/api/admin/customers").then(list => {
      if (!active) return;
      if (!Array.isArray(list) || list.some(c => !c || typeof c.id !== "string" || !c.id.trim() || typeof c.name !== "string" || !c.name.trim()) || new Set(list.map(c => c.id)).size !== list.length) {
        setState({ owner, attempt, status: "error", error: "kunde.ungueltigeAntwort", list: [] });
        return;
      }
      setState({ owner, attempt, status: "ready", list });
    }).catch(() => {
      if (active) setState({ owner, attempt, status: "error", error: "kunde.ladefehler", list: [] });
    });
    return () => { active = false; };
  }, [enabled, owner, attempt]);
  const current = enabled && state.owner === owner && state.attempt === attempt;
  return { ...(current ? state : { status: "loading", list: [] }), retry: () => setAttempt(n => n + 1) };
}

export default function CustomerAssignment({ id, editable, value = [], account, customers, onChange, disabled }) {
  const { t } = useTranslation(["benutzer", "gemeinsam"]);
  const ready = customers.status === "ready";
  const savedIds = userCustomerIds(account);
  const savedNames = Array.isArray(account?.customerNames) ? account.customerNames : [account?.customerName];
  const savedName = customerId => savedNames[savedIds.indexOf(customerId)];
  const missing = value.filter(customerId => !customers.list.some(c => c.id === customerId));
  const error = customers.status === "error" ? customers.error : ready && missing.length ? "kunde.nichtVerfuegbar" : null;
  const description = `${id}-hint${value.length > 1 ? ` ${id}-branding` : ""}${editable && error ? ` ${id}-error` : ""}`;
  const options = [...customers.list, ...missing.map(customerId => ({ id: customerId, name: savedName(customerId) || customerId }))];
  return <fieldset id={id} aria-describedby={description} aria-busy={editable && customers.status === "loading"}
    style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0, maxWidth: "100%", margin: "14px 0 0", padding: 0, border: 0, fontFamily: "'Source Sans 3', sans-serif", color: "var(--iio-text-1)" }}>
    <legend style={{ padding: 0, marginBottom: 6, fontSize: 13, fontWeight: 600 }}>{t("kunde.label")}</legend>
    {editable ? <>
      <div style={{ display: "flex", flexDirection: "column", maxHeight: 264, overflowY: "auto", minWidth: 0 }}>
        {options.map(c => <label key={c.id} style={{ display: "flex", alignItems: "center", gap: 10, minHeight: 44, padding: "0 8px", fontSize: 14, fontWeight: 400, letterSpacing: "normal", textTransform: "none", color: "var(--iio-text-1)", overflowWrap: "anywhere", cursor: disabled || !ready ? "default" : "pointer" }}>
          <input type="checkbox" checked={value.includes(c.id)} disabled={disabled || !ready}
            aria-describedby={description} aria-invalid={!!error}
            style={{ flexShrink: 0, width: 18, height: 18, margin: 0, accentColor: "var(--iio-info)" }}
            onChange={e => onChange(e.target.checked ? [...value, c.id] : value.filter(customerId => customerId !== c.id))} />
          <span style={{ minWidth: 0 }}>{c.name}</span>
        </label>)}
      </div>
      {ready && value.length === 0 && <div>{t("kunde.keiner")}</div>}
    </> : <div style={{ overflowWrap: "anywhere" }}>
      {value.length ? value.map(customerId => <div key={customerId}>{savedName(customerId) || t("kunde.unbekannt")}</div>) : t("kunde.keiner")}
    </div>}
    <div id={`${id}-hint`} style={{ fontSize: 12, lineHeight: 1.5, color: "var(--iio-text-2)" }}>{t(editable ? "kunde.hinweis" : "kunde.hinweisLesend")}</div>
    {value.length > 1 && <div id={`${id}-branding`} style={{ fontSize: 12, lineHeight: 1.5, color: "var(--iio-text-2)" }}>{t("kunde.mehrereBranding")}</div>}
    {editable && customers.status === "loading" && <div role="status">{t("kunde.laedt")}</div>}
    {editable && ready && !missing.length && customers.list.length === 0 && <div style={{ fontSize: 12, color: "var(--iio-text-2)" }}>{t("kunde.leer")}</div>}
    {editable && error && <div>
      <div id={`${id}-error`} role="alert">{t(error)}</div>
      <button type="button" disabled={disabled} onClick={customers.retry} style={{ marginTop: 6, minHeight: 44, padding: "0 12px", font: "inherit", cursor: "pointer", color: "var(--iio-text-1)", background: "var(--iio-bg-2)", border: "1px solid var(--iio-border)", borderRadius: "var(--iio-r-sm)" }}>{t("kunde.erneutLaden")}</button>
    </div>}
  </fieldset>;
}
